import {getHttpClient} from '../../modules/httpClient.js';
import {Notification} from '../../modules/notification.js';
import {Spinner} from '../../modules/spinner.js';
import {getRandomQuote} from '../../pages/courses/courseLessonItem__quizzes.js';
import {displayValidationErrors} from '../../modules/validationErrorHandler.js';

const client = getHttpClient();

/**
 * @param {HTMLElement} container
 */
function initializeQuizArea(container) {
    const quizContainer = container.querySelector('.promotionalQuiz__container');
    const carousel = quizContainer.querySelector('.promotionalQuiz__quizCarousel');
    const carouselItems = carousel.querySelectorAll('.promotionalQuiz__quizCarouselItem');
    const quizArea = quizContainer.querySelector('.promotionalQuiz__quizArea');
    const quizForm = quizArea.querySelector('form');
    const quizCompleteArea = container.querySelector('.promotionalQuiz__completeArea');
    const quizSuccessArea = quizCompleteArea.querySelector('.promotionalQuiz__success');
    const emailSentArea = quizCompleteArea.querySelector('.promotionalQuiz__emailSent');
    const emailForm = quizSuccessArea.querySelector('form');
    const quizFailureArea = quizCompleteArea.querySelector('.promotionalQuiz__failure');
    // Get quiz area progress tracker elements
    const quizAreaProgressBar = quizArea.querySelector('.js-progressTracker__actualMeter');
    const quizAreaSteps = quizArea.querySelectorAll('.progressTracker__item');

    emailForm.addEventListener('submit', function (submitEvent) {
        submitEvent.preventDefault();
        const formData = new FormData(emailForm);
        submitEmail(formData.get('email'));
    });

    /** @param {boolean} isEnabled */
    function enableLostProgressWarning(isEnabled) {
        // eslint-disable-next-line unicorn/prefer-add-event-listener
        window.onbeforeunload = isEnabled === true
            ? () => 'If you leave the page your progress will be lost.'
            : null;
    }

    /**
     * @param {number} activeIndex
     */
    function setProgressBarState(activeIndex) {
        if (activeIndex === 0) {
            return;
        }

        // We only update the quiz area tracker during the quiz flow
        const steps = quizAreaSteps;
        const progressBar = quizAreaProgressBar;
        
        if (!steps || !progressBar) {
            return;
        }

        steps.forEach((step, stepIndex) => {
            if (stepIndex === activeIndex) {
                step.classList.add('progressTracker__item--active');
            } else {
                step.classList.remove('progressTracker__item--active');

                if (stepIndex < activeIndex) {
                    step.classList.add('progressTracker__item--completed');
                }
                if (stepIndex > activeIndex) {
                    step.classList.remove('progressTracker__item--completed');
                }
            }
        });

        // Calculate the width directly based on the active step
        const progressBarWidth = (activeIndex / (steps.length - 1)) * 100;
        progressBar.style.width = `${progressBarWidth}%`;
    }

    /**
     * @param {HTMLElement} container
     * @param {Function} onSubmit
     */
    function initializeQuiz(container, onSubmit) {
        const submitButton = container.querySelector('.js-quizSubmitButton');
        const inputs = container.querySelectorAll('.js-answerChoice');

        quizCompleteArea.style.visibility = 'hidden';

        submitButton.disabled = true;

        submitButton.addEventListener('click', (event) => {
            event.preventDefault();
            onSubmit();
        });

        /**
         *
         */
        function handleChangeEvent() {
            submitButton.disabled = false;
            inputs.forEach((input) => input.removeEventListener('change', handleChangeEvent));
        }

        inputs.forEach((input) => input.addEventListener('change', handleChangeEvent));
    }

    /**
     * @param {string|number} correct
     * @param {number} total
     */
    function initializeFailureArea(correct, total) {
        quizSuccessArea.hidden = true;

        const correctCountElement = quizFailureArea.querySelector('.js-correct-count');
        const totalCountElement = quizFailureArea.querySelector('.js-total-count');
        const quoteElement = quizFailureArea.querySelector('.js-quote');

        correctCountElement.textContent = correct;
        totalCountElement.textContent = total;

        quoteElement.innerHTML = getRandomQuote().outerHTML;
    }

    /**
     * @param {string|number} correct
     * @param {string|number} total
     */
    function initializeSuccessArea(correct, total) {
        quizFailureArea.hidden = true;

        const correctCountElement = quizSuccessArea.querySelector('.js-correct-count');
        const totalCountElement = quizSuccessArea.querySelector('.js-total-count');

        correctCountElement.textContent = correct;
        totalCountElement.textContent = total;
    }

    /**
     * @param {boolean} pass
     * @param {string|number} correct
     * @param {string|number} total
     */
    async function initializeQuizCompletionArea(pass, correct, total) {
        if (pass) {
            initializeSuccessArea(correct, total);
            await animateGlobalCarouselState(quizSuccessArea.clientHeight);
        } else {
            initializeFailureArea(correct, total);
            await animateGlobalCarouselState(quizFailureArea.clientHeight);
            enableLostProgressWarning(false);
        }

        quizArea.style.marginLeft = '-100%';
        quizArea.style.height = 0;
    }

    /**
     * @param {number} height
     */
    function animateGlobalCarouselState(height) {
        quizCompleteArea.style.visibility = '';
        const duration = 400;
        return new Promise((resolve) => {
            const animation = quizArea.animate(
                {
                    marginLeft: [0, '-100%'],
                    opacity: [1.0, 0],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            quizCompleteArea.animate(
                {
                    opacity: [0, 1.0],
                },
                {
                    duration,
                    easing: 'ease-in',
                }
            );

            quizContainer.animate(
                {
                    height: [`${quizContainer.clientHeight}px`, `${height}px`],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            animation.onfinish = () => {
                quizContainer.style.height = '';
                resolve();
            };
        });
    }

    /**
     * @param {number} activeIndex
     */
    function animateCarouselState(activeIndex) {
        const duration = 400;

        return new Promise((resolve) => {
            const initialLeftMargin = getComputedStyle(carouselItems[0]).marginLeft;
            const oldQuiz = carouselItems[activeIndex - 1];
            const newQuiz = carouselItems[activeIndex];
            const animation = carouselItems[0].animate(
                {
                    marginLeft: [`${initialLeftMargin}`, `${-100 * activeIndex}%`],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            oldQuiz.animate(
                {
                    opacity: [1.0, 0],
                },
                {
                    duration,
                    easing: 'ease-in',
                }
            );

            newQuiz.style.visibility = '';
            newQuiz.style.height = '';
            newQuiz.animate(
                {
                    opacity: [0, 1.0],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            carousel.animate(
                {
                    height: [
                        `${oldQuiz.querySelector('.promotionalQuiz__quizCarouselItemContent').clientHeight}px`,
                        `${newQuiz.querySelector('.promotionalQuiz__quizCarouselItemContent').clientHeight}px`,
                    ],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            animation.onfinish = () => {
                resolve();
            };
        });
    }

    /**
     * @param {number} activeIndex
     * @param {boolean} animated
     */
    async function setCarouselState(activeIndex, animated = true) {
        setProgressBarState(activeIndex);

        /**
         * If the visitor made progress by answering at least the first question,
         * warn about leaving the page as it may lead to lost progress.
         */
        if (activeIndex === 1) {
            enableLostProgressWarning(true);
        }

        if (animated) {
            await animateCarouselState(activeIndex);
        }

        carouselItems[0].style.marginLeft = `${-100 * activeIndex}%`;

        carouselItems.forEach((carouselItem, carouselItemIndex) => {
            if (carouselItemIndex === activeIndex) {
                carouselItem.style.visibility = '';
                carouselItem.style.height = '';
            } else {
                carouselItem.style.visibility = 'hidden';
                carouselItem.style.height = 0;
            }
        });
    }

    /**
     *
     */
    async function submitAnswers() {
        const spinner = new Spinner(container);
        spinner.start();

        const formData = new FormData(quizForm);
        const answers = {};

        const token = formData.get('_token');

        formData.delete('_token');

        formData.forEach((value, question) => answers[question] = value);

        const {correctCount, questionCount, passed, notificationUrl} = await client.post(quizForm.action, {
            _token: token,
            answers,
        });

        if (passed) {
            emailForm.action = notificationUrl;
        }

        initializeQuizCompletionArea(passed, correctCount, questionCount);

        spinner.stop();
    }

    /**
     * @param {string} email
     */
    function initializeEmailConfirmationArea(email) {
        const emailElement = emailSentArea?.querySelector('.js-email');

        if (emailElement) {
            emailElement.textContent = email;
        }
    }

    /**
     *
     */
    async function animateEmailSubmissionCarousel() {
        emailSentArea.style.visibility = '';
        const duration = 400;
        return new Promise((resolve) => {
            const animation = quizSuccessArea.animate(
                {
                    marginLeft: [0, '-100%'],
                    opacity: [1.0, 0],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            emailSentArea.animate(
                {
                    opacity: [0, 1.0],
                },
                {
                    duration,
                    easing: 'ease-in',
                }
            );

            quizContainer.animate(
                {
                    height: [`${quizSuccessArea.clientHeight}px`, `${emailSentArea.clientHeight}px`],
                },
                {
                    duration,
                    easing: 'ease-out',
                }
            );

            animation.onfinish = () => {
                quizContainer.style.height = '';
                quizSuccessArea.hidden = true;
                resolve();
            };
        });
    }

    /**
     * @param {string} email
     * @returns {Promise<void>}
     */
    async function submitEmail(email) {
        const spinner = new Spinner(container);
        spinner.start();

        let response = null;

        try {
            response = await client.post(emailForm.action, {
                email,
            });
        } catch (error) {
            if (error.status === 422) {
                displayValidationErrors(error.errorBody, quizSuccessArea);
            } else {
                Notification.error(error.message);
            }
        } finally {
            spinner.stop();
        }

        if (response !== null) {
            initializeEmailConfirmationArea(email);
            animateEmailSubmissionCarousel();
        }
    }

    carouselItems.forEach((quiz, index) =>
        initializeQuiz(quiz, async () => {
            if (index < carouselItems.length - 1) {
                setCarouselState(index + 1);
            } else {
                submitAnswers();
            }
        })
    );
    setCarouselState(0, false);
}

document.querySelectorAll('.js-promotionalQuizzesContainer').forEach(initializeQuizArea);
